import { PageSize } from '@/constant';
import {
	GetForeignUserListParams,
	IForeignUser,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { formatForeignUserList, RootState } from '..';
import {
	IForeignUserDetailStore,
	IPayloadCreateForeignUserRequest,
	IPayloadDeleteForeignUserRequest,
	IPayloadGetForeignUserDetailRequest,
	IPayloadGetForeignUserDetailSuccess,
	IPayloadGetForeignUserListRequest,
	IPayloadGetForeignUserListSuccess,
} from './foreign-user.type';

const InitParams: GetForeignUserListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	foreignUserList: [] as IForeignUserDetailStore[],
	total: 0,
	params: InitParams,
	foreignUserDetail: {} as IForeignUserDetailStore,
};

const foreignUserSlice = createSlice({
	initialState,
	name: 'foreignUser',
	reducers: {
		/**---- GET FOREIGN_USER LIST START ---- */
		getForeignUserListRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignUserListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getForeignUserListSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignUserListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.foreignUserList = content;
			state.total = total;
		},
		getForeignUserListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.foreignUserList = [];
		},
		/**---- GET FOREIGN_USER LIST END ---- */

		/**---- CREATE FOREIGN_USER START ---- */
		createForeignUserRequest: (
			state,
			action: PayloadAction<IPayloadCreateForeignUserRequest>
		) => {
			state.fetching = true;
		},
		createForeignUserSuccess: (state) => {
			state.fetching = false;
		},
		createForeignUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE FOREIGN_USER END ---- */

		/**---- DELETE FOREIGN_USER START ---- */
		deleteForeignUserRequest: (
			state,
			action: PayloadAction<IPayloadDeleteForeignUserRequest>
		) => {
			state.fetching = true;
		},
		deleteForeignUserSuccess: (state) => {
			state.fetching = false;
		},
		deleteForeignUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE FOREIGN_USER START ---- */

		/**---- GET FOREIGN_USER DETAIL START ---- */
		getForeignUserDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignUserDetailRequest>
		) => {
			state.fetching = true;
		},
		getForeignUserDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignUserDetailSuccess>
		) => {
			const { foreignUserDetail } = action.payload;
			state.fetching = false;
			state.foreignUserDetail = foreignUserDetail;
		},
		getForeignUserDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET FOREIGN_USER DETAIL END ---- */

		/**---- GET SYSTEM NOTICE DETAIL START ---- */
		updateForeignUserSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IForeignUser>>
		) => {
			const { data } = action.payload;
			const foreignUserList = [...state.foreignUserList];
			state.foreignUserList = formatForeignUserList(
				mergeListItemUpdated<IForeignUserDetailStore, IForeignUser>(
					foreignUserList,
					data
				)
			);
		},
		updateForeignUserDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IForeignUser>>
		) => {
			const { data } = action.payload;
			const foreignUserDetail = { ...state.foreignUserDetail };
			state.foreignUserDetail = mergeItemUpdated(foreignUserDetail, data);
		},
		/**---- GET SYSTEM NOTICE DETAIL END ---- */
	},
});

export const ForeignUserSelectors = {
	selectFetching: (state: RootState) => state.foreignUser.fetching,
	selectParams: (state: RootState) => state.foreignUser.params,
	selectList: (state: RootState) => state.foreignUser.foreignUserList,
	selectTotal: (state: RootState) => state.foreignUser.total,
	selectDetail: (state: RootState) => state.foreignUser.foreignUserDetail,
};

export const ForeignUserActions = foreignUserSlice.actions;
export const ForeignUserReducer = foreignUserSlice.reducer;
